import moment from 'moment'
import { Calendar, momentLocalizer, View } from 'react-big-calendar'
import {
    Alert,
    AlertType,
    GetLocale,
    Modal,
} from '@cuatroochenta/co-admin-react-library'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import CustomToolbar from './components/CustomToolbar/CustomToolbar'
import CustomEvent from './components/CustomEvent/CustomEvent'
import { translate } from '../../../I18n/i18n'
import SaveEventModal from './components/SaveEventModal/SaveEventModal'
import { Event } from '../../model/domain/event'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'moment/locale/es'
import 'moment/locale/it'
import './CalendarView.scss'
import { ShareCenterOption } from '../../model/domain/share-option'
import { User } from '../../model/domain/user'

interface CalendarViewProps {
    user?: User
    mappedEvents
    shareOptions?: ShareCenterOption[]
    loadingShareOptions: boolean
    view: View
    showSaveModal: boolean
    showDeleteModal: boolean
    loading: boolean
    error: string | null
    saveSuccess: boolean
    deleteSuccess: boolean
    loadingDetail: boolean
    event: Event | null
    onView: (view: View) => void
    onNavigate: (newDate: Date) => void
    onOpenCreateEvent: () => void
    onCancelCreateEvent: () => void
    onConfirmCreateEvent: (event: Event) => void
    onOpenEditEvent: (eventId: number) => void
    onOpenDeleteEvent: (eventId: number) => void
    onCancelDeleteEvent: () => void
    onConfirmDeleteEvent: () => void
}

export default function CalendarView({
    user,
    mappedEvents,
    shareOptions,
    loadingShareOptions,
    view,
    showSaveModal,
    showDeleteModal,
    loading,
    error,
    saveSuccess,
    deleteSuccess,
    loadingDetail,
    event,
    onView,
    onNavigate,
    onOpenCreateEvent,
    onCancelCreateEvent,
    onConfirmCreateEvent,
    onOpenEditEvent,
    onOpenDeleteEvent,
    onCancelDeleteEvent,
    onConfirmDeleteEvent,
}: CalendarViewProps) {
    const { t } = useTranslation()

    const localizer = useMemo(() => {
        const currentLocale = GetLocale()
        moment.locale(currentLocale)
        moment.updateLocale(currentLocale, { week: { dow: 1 } })
        return momentLocalizer(moment)
    }, [])

    const showAlert = useMemo(() => {
        return !!error || saveSuccess || deleteSuccess
    }, [error, saveSuccess, deleteSuccess])

    return (
        <>
            <Alert
                show={showAlert}
                type={error ? AlertType.DANGER : AlertType.SUCCESS}
                message={
                    error
                        ? error
                        : saveSuccess
                        ? translate(t, 'calendar.TR_SAVE_SUCCESS')
                        : translate(t, 'calendar.TR_DELETE_SUCCESS')
                }
            />
            <Calendar
                localizer={localizer}
                events={mappedEvents}
                style={{ height: showAlert ? '75vh' : '85vh' }}
                views={{ month: true, week: true }}
                view={view}
                onView={onView}
                onNavigate={onNavigate}
                components={{
                    toolbar: (props) => (
                        <CustomToolbar
                            onViewChange={onView}
                            onCreate={onOpenCreateEvent}
                            loading={loading}
                            {...props}
                        />
                    ),
                    event: (props) => (
                        <CustomEvent
                            user={user}
                            onEdit={onOpenEditEvent}
                            onDelete={onOpenDeleteEvent}
                            {...props}
                        />
                    ),
                }}
                messages={{
                    showMore: (count) =>
                        translate(t, 'calendar.TR_MORE_EVENTS', { count }),
                }}
                eventPropGetter={(event: Event | any) => ({
                    style: { backgroundColor: event.color },
                })}
                tooltipAccessor={null}
                popup
            />
            <SaveEventModal
                show={showSaveModal}
                loading={loadingDetail}
                event={event}
                shareOptions={shareOptions}
                loadingShareOptions={loadingShareOptions}
                onCancel={onCancelCreateEvent}
                onConfirm={onConfirmCreateEvent}
            />
            <Modal
                show={showDeleteModal}
                title={translate(t, 'calendar.TR_DELETE_MODAL_TITLE')}
                children={
                    <p>{translate(t, 'calendar.TR_DELETE_MODAL_MESSAGE')}</p>
                }
                acceptHandler={onConfirmDeleteEvent}
                onClose={onCancelDeleteEvent}
            />
        </>
    )
}
